import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _taggedTemplateLiteral from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/taggedTemplateLiteral.js";

var _templateObject, _templateObject2;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import isPropValid from '@emotion/is-prop-valid';
import { AsyncButton, Bingmap, SaveStates, Tooltip } from '@infl-ts/ui-advocate-components';
import { useContainerMediaQuery } from '@infl-ts/util-block-width-context';
import { usePreviousProp, useToggle } from '@infl-ts/util-hooks';
import { useMutationMapper } from '@infl-ts/util-use-mutation-mapper';
import PlaceIcon from '@mui/icons-material/Place';
import { Alert, Box, Button, buttonClasses, styled } from '@mui/material';
import * as E from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BING_MAPS_API_KEY } from '../../../../env';
import StageLayout from '../stage-layout';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var INFLUITIVE_TORONTO_COORDS = {
  latitude: 43.6479318,
  longitude: -79.3948223
};

var accessErrorMessage = function accessErrorMessage() {
  return /maven/.exec(window.navigator.userAgent) ? 'Please enable location access in the app settings to check in' : 'You must give your browser location permissions to check in';
};

var useNavigatorLocation = function useNavigatorLocation() {
  var _useState = useState(null),
      coords = _useState[0],
      setCoords = _useState[1];

  useEffect(function () {
    var watchId = window.navigator.geolocation.watchPosition(function (_ref) {
      var _ref$coords = _ref.coords,
          latitude = _ref$coords.latitude,
          longitude = _ref$coords.longitude;
      return setCoords({
        latitude: latitude,
        longitude: longitude
      });
    }, function () {
      return setCoords(null);
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
    return function () {
      if (watchId) window.navigator.geolocation.clearWatch(watchId);
    };
  }, []);
  return coords;
};

var isUserLocationWithInArea = function isUserLocationWithInArea(userLocation, latitude, longitude, fenceRadius) {
  var lat1 = userLocation.latitude,
      lon1 = userLocation.longitude;
  var lat2 = latitude;
  var lon2 = longitude;
  var factor = Math.PI / 180;
  var dLat = (lat2 - lat1) * factor;
  var dLon = (lon2 - lon1) * factor; // Haversine formula: https://www.movable-type.co.uk/scripts/latlong.html

  var angularDistance = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * factor) * Math.cos(lat2 * factor) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  var chordLength = 2 * Math.atan2(Math.sqrt(angularDistance), Math.sqrt(1 - angularDistance));
  var EARTH_RADIUS_KM = 6371000; // approximate radius in meters

  var distance = EARTH_RADIUS_KM * chordLength;
  return distance <= fenceRadius;
};

var ResizableMap = styled(Box, {
  shouldForwardProp: isPropValid
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: relative;\n  height: 400px;\n  min-width: 192px;\n  width: ", ";\n"])), function (p) {
  return p.isMobile ? '100%' : p.isTablet ? '320px' : '768px';
});
var FindMeTooltip = styled(Tooltip)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  margin: ", ";\n  position: absolute;\n  bottom: ", ";\n  left: ", ";\n  z-index: ", ";\n"])), function (p) {
  return p.theme.spacing(1);
}, function (p) {
  return p.theme.spacing(1);
}, function (p) {
  return p.theme.spacing(1);
}, function (p) {
  return p.theme.zIndex._100;
});

var Checkin = function Checkin(_ref2) {
  var stage = _ref2.stage,
      activity = _ref2.activity,
      completeStage = _ref2.completeStage,
      completeStageStatus = _ref2.completeStageStatus,
      prospect = _ref2.prospect;
  var isMobile = useContainerMediaQuery({
    maxWidth: 'sm'
  });
  var isTablet = useContainerMediaQuery({
    maxWidth: 'md'
  });

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var fenceRadius = stage.fenceRadius,
      latitude = stage.latitude,
      longitude = stage.longitude,
      location = stage.location;
  var targetLocation = latitude && longitude ? {
    latitude: latitude,
    longitude: longitude
  } : INFLUITIVE_TORONTO_COORDS;

  var _useToggle = useToggle(),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      isTrackingUser = _useToggle2[0],
      trackUser = _useToggle2[1].on;

  var _useState2 = useState(null),
      map = _useState2[0],
      setMap = _useState2[1];

  var _useState3 = useState({
    latitude: 0,
    longitude: 0
  }),
      mapCenter = _useState3[0],
      setMapCenter = _useState3[1];

  var _useToggle3 = useToggle(),
      _useToggle4 = _slicedToArray(_useToggle3, 2),
      checkedIn = _useToggle4[0],
      checkIn = _useToggle4[1].on;

  var userLocation = useNavigatorLocation();
  var previousMap = usePreviousProp(map);
  var previousUserLocation = usePreviousProp(userLocation);
  var userLocationWithInArea = userLocation && latitude && longitude && fenceRadius && isUserLocationWithInArea(userLocation, latitude, longitude, fenceRadius);
  useEffect(function () {
    if (userLocation && isTrackingUser) setMapCenter({
      latitude: userLocation.latitude,
      longitude: userLocation.longitude
    });
    if (userLocationWithInArea) checkIn();
  }, [userLocation, userLocationWithInArea]);
  useEffect(function () {
    if (map && userLocation && (map && !previousMap || userLocation && !previousUserLocation)) {
      var locations = [new Microsoft.Maps.Location(userLocation.latitude, userLocation.longitude), new Microsoft.Maps.Location(targetLocation.latitude, targetLocation.longitude)];
      var rect = Microsoft.Maps.LocationRect.fromLocations(locations);
      map.setView({
        bounds: rect,
        padding: 40
      });
      setTimeout(trackUser, 1000);
    }
  }, [previousUserLocation, userLocation, map, targetLocation]);

  var findCheckin = function findCheckin() {
    map && map.setView({
      center: new Microsoft.Maps.Location(targetLocation.latitude, targetLocation.longitude),
      zoom: 10
    });
  };

  var Saving = SaveStates.Saving,
      Default = SaveStates.Default,
      Success = SaveStates.Success,
      Error = SaveStates.Error;
  var mutationValue = useMutationMapper(completeStageStatus, function (_ref3) {
    var completeStage = _ref3.completeStage;
    return completeStage.__typename === 'CompleteStageErrors' ? E.left(completeStage) : E.right(completeStage);
  }, [Saving, Success, Error, Default]);
  return /*#__PURE__*/_jsxs(StageLayout, {
    stage: stage,
    prospect: prospect,
    actions: /*#__PURE__*/_jsx(Tooltip, {
      title: checkedIn ? '' : t('You must be in the highlighted area to check in'),
      children: /*#__PURE__*/_jsx("span", {
        children: /*#__PURE__*/_jsx(AsyncButton, {
          status: mutationValue,
          onClick: function onClick() {
            return completeStage({
              checkInInput: _objectSpread({
                stageID: stage.id,
                activityID: activity === null || activity === void 0 ? void 0 : activity.id
              }, userLocation ? {
                latitude: String(userLocation.latitude),
                longitude: String(userLocation.longitude)
              } : {})
            });
          },
          disabled: !checkedIn,
          text: ['Checking in...', 'Checked in', stage.completionText || 'Check in', stage.completionText || 'Check in'],
          sx: _defineProperty({}, "&.".concat(buttonClasses.contained, ".").concat(buttonClasses.disabled), {
            background: function background(t) {
              return mutationValue === 'success' ? t.palette.success.main : t.palette.primary.light;
            },
            color: function color(t) {
              return t.palette.common.white;
            }
          })
        })
      })
    }),
    "data-element": "StageLayout",
    "data-component": "Checkin",
    "data-source-file": "index.tsx",
    children: [/*#__PURE__*/_jsxs(ResizableMap, {
      isMobile: isMobile,
      isTablet: isTablet,
      "data-element": "ResizableMap",
      "data-component": "Checkin",
      "data-source-file": "index.tsx",
      children: [/*#__PURE__*/_jsx(Bingmap, {
        apiKey: BING_MAPS_API_KEY,
        onInitialize: setMap,
        center: mapCenter,
        zoom: 13,
        polygons: [{
          center: targetLocation,
          radius: fenceRadius !== null && fenceRadius !== void 0 ? fenceRadius : 1,
          points: 360,
          options: {
            fillColor: 'rgba(255,182,193,0.5)',
            strokeColor: 'rgba(255,182,193,1)',
            strokeThickness: 2,
            title: location !== null && location !== void 0 ? location : ''
          }
        }],
        pushPins: userLocation ? [{
          location: userLocation,
          options: {
            color: 'red',
            title: 'You'
          }
        }] : [],
        "data-element": "Bingmap",
        "data-component": "Checkin",
        "data-source-file": "index.tsx"
      }), /*#__PURE__*/_jsx(FindMeTooltip, {
        title: userLocation ? '' : t('You must allow location access'),
        arrow: true,
        disableFocusListener: true,
        "data-element": "FindMeTooltip",
        "data-source-file": "index.tsx",
        children: /*#__PURE__*/_jsx(Box, {
          "data-element": "Box",
          "data-source-file": "index.tsx",
          children: /*#__PURE__*/_jsx(Button, {
            sx: {
              borderRadius: 8,
              border: '1px solid',
              borderColor: function borderColor(t) {
                return t.palette.neutral.main;
              },
              background: function background(t) {
                return t.palette.common.white;
              }
            },
            startIcon: /*#__PURE__*/_jsx(PlaceIcon, {}),
            variant: "contained",
            color: "inherit",
            onClick: findCheckin,
            disabled: userLocation === null,
            "data-element": "Button",
            "data-source-file": "index.tsx",
            children: t('Find checkin')
          })
        })
      })]
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        mt: 2
      },
      "data-element": "Box",
      "data-source-file": "index.tsx",
      children: checkedIn ? null : userLocation ? /*#__PURE__*/_jsx(Alert, {
        severity: "info",
        children: t('You must be in the highlighted area to check in')
      }) : /*#__PURE__*/_jsx(Alert, {
        severity: "error",
        children: t(accessErrorMessage())
      })
    })]
  });
};

export default Checkin;